import { Box } from "@twilio-paste/core/box";
import { Table, TBody, Td, Th, THead, Tr } from "@twilio-paste/core/table";
import { graphql, Link, useStaticQuery } from "gatsby";
import { SitesQuery } from "queries";
import React from "react";
import { Element } from "react-scroll";
import { Property } from "types";
import { getLastValidElementFromPath } from "../../helper";
import { AnchoredHeading, Code } from "../../shared/components";
import { codify } from "../../utils/stringUtils";

const headers = ["Name", "Type", "Description", "Optional", "Default"];
const query = graphql`
    {
        pages: allSitePage {
            nodes {
                path
            }
        }
    }
`;

interface ComponentPropertiesProps {
    properties: Property[];
    heading: string;
    uniqueName: string;
}

function ComponentProperties(props: ComponentPropertiesProps) {
    const { properties, heading, uniqueName } = props;
    const { pages }: SitesQuery = useStaticQuery(query);

    if (!properties.length) {
        return null;
    }

    return (
        <Element id={uniqueName} name={uniqueName} style={{ scrollMarginTop: "calc(69px + 2rem)", overflow: "auto" }}>
            <Box paddingBottom="space200" as="section">
                <AnchoredHeading as="h3" variant="heading30">
                    {heading}
                </AnchoredHeading>
                <Box overflow="auto">
                    <Box minWidth={["size80", undefined, undefined]}>
                        <Table>
                            <THead>
                                <Tr>
                                    {headers.map((header) => (
                                        <Th key={header}>{header}</Th>
                                    ))}
                                </Tr>
                            </THead>
                            <TBody>
                                {properties.map((prop) => {
                                    let propType: string | undefined | React.ReactElement;
                                    if (prop.type) {
                                        const extracted =
                                            prop.type.names[0].match(/(?<=<)(.*)(?=>)/g) || prop.type.names;
                                        propType = prop.type.names.join(" | ");

                                        const pathForComponent = pages.nodes.filter(
                                            ({ path }: { path: string }) =>
                                                getLastValidElementFromPath(path) === extracted[0]
                                        );
                                        if (pathForComponent.length) {
                                            propType = <Link to={pathForComponent[0].path}>{propType}</Link>;
                                        }
                                    }
                                    return (
                                        <Tr key={prop.name}>
                                            <Td>{prop.name}</Td>
                                            <Td>
                                                <Code as="code">{propType}</Code>
                                            </Td>
                                            <Td>
                                                <div dangerouslySetInnerHTML={{ __html: codify(prop.description) }} />
                                            </Td>
                                            <Td>{prop.optional ? "Yes" : "No"}</Td>
                                            <Td>
                                                {prop.defaultvalue ? <Code as="code">{prop.defaultvalue}</Code> : ""}
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </TBody>
                        </Table>
                    </Box>
                </Box>
            </Box>
        </Element>
    );
}

export { ComponentProperties };
